import { useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import clsx from 'clsx';
import {
  BookOpenText,
  Eye,
  LayoutDashboard,
  LogOut,
  Monitor,
  Moon,
  Settings2,
  Sun,
} from 'lucide-react';
import { useAuth, useMe } from '../../lib/auth.js';
import { useTheme, type ThemeChoice } from '../../lib/theme.js';
import { Avatar, Button, clsx as cx } from '../ui.js';
import { PRODUCT_NAME, TAGLINE, Wordmark } from '../Brand.js';

export { Wordmark };

export function AppShell({ children }: { children: ReactNode }) {
  const me = useMe();
  const { logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const showConsoleLink =
    me.capabilities.isAdmin ||
    me.capabilities.isInstructor ||
    me.capabilities.isCourseAdmin ||
    me.capabilities.isGuideWriter;

  return (
    <div className="min-h-full flex flex-col bg-canvas">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <header className="app-bar sticky top-0 z-30 border-b border-chrome-line bg-chrome text-chrome-ink">
        <div className="mx-auto max-w-[80rem] px-4 sm:px-6 h-14 flex items-center gap-3 sm:gap-4">
          <Link
            to="/"
            className="flex items-center shrink-0 rounded-control"
            aria-label={`${PRODUCT_NAME} — home`}
          >
            <span className="sm:hidden">
              <Wordmark onChrome showName={false} />
            </span>
            <span className="hidden sm:inline-flex">
              <Wordmark onChrome />
            </span>
          </Link>

          <span aria-hidden className="hidden sm:block h-6 w-px bg-chrome-line" />

          <nav className="flex items-center gap-1" aria-label="Main">
            <ShellLink to="/" icon={<LayoutDashboard size={15} />} exact>
              My learning
            </ShellLink>
            <ShellLink to="/guides" icon={<BookOpenText size={15} />}>
              Guides
            </ShellLink>
            {showConsoleLink ? (
              <ShellLink to="/admin" icon={<Settings2 size={15} />}>
                Console
              </ShellLink>
            ) : null}
          </nav>

          <div className="flex-1" />

          <ThemeToggle />

          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className="flex items-center gap-2 rounded-control px-1.5 py-1 transition-colors duration-150 ease-out hover:bg-chrome-2"
            >
              <Avatar name={me.user.name} size={28} />
              <span className="hidden md:block text-sm text-chrome-ink max-w-36 truncate">
                {me.user.name}
              </span>
            </button>

            {menuOpen ? (
              <>
                <button
                  type="button"
                  aria-hidden
                  tabIndex={-1}
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setMenuOpen(false)}
                />
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-64 z-20 rounded-card border border-line bg-surface shadow-pop p-1"
                >
                  <div className="px-3 py-2 border-b border-line mb-1">
                    <p className="text-sm font-medium text-ink truncate">{me.user.name}</p>
                    <p className="text-xs text-ink-subtle truncate">{me.user.email}</p>
                    <p className="eyebrow mt-1.5">
                      {me.capabilities.isAdmin
                        ? 'Administrator'
                        : me.capabilities.isCourseAdmin
                          ? 'Course admin'
                          : me.capabilities.isInstructor
                          ? `Instructor · ${me.capabilities.assignedSessionCount} session${
                              me.capabilities.assignedSessionCount === 1 ? '' : 's'
                            }`
                          : 'Learner'}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void logout()}
                    className="w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-control transition-colors duration-150 ease-out hover:bg-surface-2 text-ink"
                  >
                    <LogOut size={15} aria-hidden />
                    Sign out
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </header>

      {me.viewAsBy ? <ViewAsBanner /> : null}

      <main id="main" key={location.pathname} className="flex-1">
        {children}
      </main>

      <footer className="border-t border-line py-5 mt-10">
        <div className="mx-auto max-w-[80rem] px-4 sm:px-6 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="eyebrow">
            {PRODUCT_NAME} · {TAGLINE}
          </span>
          <span className="text-xs text-ink-subtle">
            Nothing here is graded · restricted to the AuxoAI directory
          </span>
        </div>
      </footer>
    </div>
  );
}

/**
 * Below 640px the label is dropped and the icon carries the link — there are
 * only two destinations, and at 320px the labelled pair pushes the account
 * menu off the header. The accessible name stays on the anchor either way.
 */
function ShellLink({
  to,
  children,
  icon,
  exact = false,
}: {
  to: string;
  children: string;
  icon?: ReactNode;
  exact?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={exact}
      aria-label={children}
      title={children}
      className={({ isActive }) =>
        cx(
          'relative flex items-center gap-1.5 rounded-control px-2.5 py-1.5 text-sm whitespace-nowrap',
          'transition-colors duration-150 ease-out',
          'after:absolute after:inset-x-2 after:-bottom-3 after:h-0.5 after:rounded-full',
          isActive
            ? 'text-chrome-ink font-semibold bg-chrome-2 after:bg-chrome-accent'
            : 'text-chrome-muted font-medium hover:text-chrome-ink hover:bg-chrome-2 after:bg-transparent',
        )
      }
    >
      {icon}
      <span className="hidden sm:inline">{children}</span>
    </NavLink>
  );
}

/** Lives on the slate chrome — the top bar here and on the sign-in page. */
export function ThemeToggle() {
  const { choice, setChoice } = useTheme();
  const order: ThemeChoice[] = ['system', 'light', 'dark'];
  const icons: Record<ThemeChoice, ReactNode> = {
    system: <Monitor size={15} />,
    light: <Sun size={15} />,
    dark: <Moon size={15} />,
  };
  const labels: Record<ThemeChoice, string> = {
    system: 'Match system theme',
    light: 'Light theme',
    dark: 'Dark theme',
  };

  return (
    <div
      className="hidden sm:flex items-center gap-0.5 rounded-control border border-chrome-line p-0.5"
      role="group"
      aria-label="Colour theme"
    >
      {order.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => setChoice(c)}
          aria-pressed={choice === c}
          title={labels[c]}
          aria-label={labels[c]}
          className={clsx(
            'grid place-items-center h-6 w-7 rounded-[0.25rem] transition-colors duration-150 ease-out',
            choice === c
              ? 'bg-chrome-2 text-chrome-ink'
              : 'text-chrome-muted hover:text-chrome-ink',
          )}
        >
          {icons[c]}
        </button>
      ))}
    </div>
  );
}

/** Standard page container so learner pages line up with each other. */
export function PageContainer({
  children,
  className,
  wide = false,
}: {
  children: ReactNode;
  className?: string;
  wide?: boolean;
}) {
  return (
    <div
      className={clsx(
        'mx-auto px-4 sm:px-6 py-7',
        wide ? 'max-w-[92rem]' : 'max-w-[80rem]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PageHeading({
  eyebrow,
  title,
  description,
  actions,
  backTo,
  backLabel,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  backTo?: string;
  backLabel?: string;
}) {
  return (
    <div className="mb-6">
      {backTo ? (
        <Link
          to={backTo}
          className="text-sm text-ink-muted hover:text-ink inline-flex items-center gap-1 transition-colors duration-150 ease-out"
        >
          <span aria-hidden>←</span>
          {backLabel ?? 'Back'}
        </Link>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-4 mt-2">
        <div className="min-w-0">
          {eyebrow ? <p className="eyebrow mb-2">{eyebrow}</p> : null}
          <h1 className="text-2xl font-semibold tracking-tight text-ink wrap-anywhere">{title}</h1>
          {description ? (
            <p className="text-sm text-ink-muted mt-1.5 max-w-prose leading-relaxed">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
      </div>
    </div>
  );
}

export { Button };

/**
 * Always on screen while an admin views the platform as someone else, so the
 * view is never mistaken for their own — and the way out is one click away.
 */
function ViewAsBanner() {
  const me = useMe();
  const { stopViewAs } = useAuth();
  const [leaving, setLeaving] = useState(false);
  const role = me.capabilities.isAdmin
    ? 'an admin'
    : me.capabilities.isCourseAdmin
      ? 'a course admin'
      : me.capabilities.isInstructor
        ? 'a trainer'
        : 'a learner';

  return (
    <div
      role="status"
      className="sticky top-14 z-20 border-b border-warn/40 bg-warn-wash text-warn"
    >
      <div className="mx-auto flex max-w-[80rem] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 text-sm sm:px-6">
        <Eye size={16} aria-hidden className="shrink-0" />
        <p className="min-w-0 flex-1">
          <span className="font-semibold">Viewing as {me.user.name}</span> ({me.user.email}), who
          is {role}. Read-only — nothing can be changed until you stop.
        </p>
        <Button
          size="sm"
          variant="secondary"
          loading={leaving}
          onClick={() => {
            setLeaving(true);
            void stopViewAs();
          }}
        >
          Stop viewing as {me.user.name.split(' ')[0]}
        </Button>
      </div>
    </div>
  );
}
